import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Search, X } from "lucide-react";
import SEO from "@/components/SEO";
import { fetchPublicPosts, fetchTopics, searchPublicPosts } from "@/lib/api";
import { BOOKING_URL } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";
import { track } from "@/lib/analytics";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Blog search
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = no search active
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPublicPosts(), fetchTopics()])
      .then(([postsData, topicsData]) => {
        setPosts(Array.isArray(postsData) ? postsData : []);
        setTopics(topicsData.topics || []);
      })
      .catch(() => {
        setPosts([]);
        setTopics([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // 300ms debounce on the search input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!debounced) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    searchPublicPosts(debounced, 30)
      .then((data) => {
        if (cancelled) return;
        setSearchResults(Array.isArray(data?.results) ? data.results : []);
        track("blog_search", { q: debounced, results: data?.count ?? 0 });
      })
      .catch(() => {
        if (cancelled) return;
        setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const isSearching = debounced.length > 0;
  const visiblePosts = useMemo(
    () => (isSearching ? searchResults || [] : posts),
    [isSearching, searchResults, posts],
  );

  return (
    <main data-testid="blog-page" className="bg-white">
      <SEO
        title="Journal — Clinical insight from our Therapists"
        description="Honest, educational writing on pain, recovery, mobility and what Clinical Massage Therapy can — and cannot — do for you."
        path="/blog"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            name: "Ashlan Clinic Journal",
            url: absoluteUrl("/blog"),
            blogPost: posts.slice(0, 12).map((p) => ({
              "@type": "BlogPosting",
              headline: p.title,
              url: absoluteUrl(`/blog/${p.slug}`),
              datePublished: p.published_at,
              image: p.featured_image_url,
              articleSection: p.category,
            })),
          },
        ]}
      />
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <span className="ash-eyebrow">Blog</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            Clinical insight from our therapists.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-2xl leading-relaxed">
            Honest, educational writing on pain, recovery, mobility and what
            Clinical Massage Therapy can — and cannot — do for you.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Search bar */}
          <div className="mb-10" data-testid="blog-search-wrap">
            <label htmlFor="blog-search-input" className="ash-eyebrow block mb-3">
              Search the Journal
            </label>
            <div className="relative max-w-2xl">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]"
                aria-hidden="true"
              />
              <input
                id="blog-search-input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Try “sciatica”, “sleep”, “pregnancy”…"
                aria-label="Search journal posts"
                data-testid="blog-search-input"
                className="w-full pl-12 pr-12 py-3.5 border border-[#e5e7eb] focus:outline-none focus:border-[#3A7A8C] text-[#080808] placeholder:text-[#9CA3AF] transition-colors"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  data-testid="blog-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#6B7280] hover:text-[#080808]"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {isSearching && (
              <p
                className="mt-3 text-sm text-[#3C4048]"
                data-testid="blog-search-status"
                aria-live="polite"
              >
                {searching
                  ? `Searching for “${debounced}”…`
                  : `${(searchResults || []).length} ${
                      (searchResults || []).length === 1 ? "result" : "results"
                    } for “${debounced}”.`}
              </p>
            )}
          </div>

          {loading && !isSearching && (
            <p data-testid="blog-loading" className="text-[#3C4048]">
              Loading…
            </p>
          )}

          {!loading && !isSearching && posts.length === 0 && (
            <div
              data-testid="blog-empty"
              className="border border-[#e5e7eb] p-12 bg-[#F5F5F5] max-w-2xl"
            >
              <span className="ash-eyebrow">Coming Soon</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">
                Our first journal entries are being prepared.
              </h2>
              <p className="mt-4 text-[#3C4048] leading-relaxed">
                In the meantime, you can book a session directly with one of our
                therapists.
              </p>
              <a
                href={BOOKING_URL}
                className="ash-btn-primary mt-6"
              >
                Book Your Session <ArrowUpRight size={16} />
              </a>
            </div>
          )}

          {isSearching && !searching && (searchResults || []).length === 0 && (
            <div
              data-testid="blog-search-empty"
              className="border border-[#e5e7eb] p-10 bg-[#F5F5F5] max-w-2xl"
            >
              <p className="text-[#3C4048] leading-relaxed">
                No posts matched <span className="font-semibold">“{debounced}”</span>. Try a
                broader keyword such as <em>pain</em>, <em>recovery</em> or <em>posture</em>.
              </p>
            </div>
          )}

          {!isSearching && topics.length > 0 && (
            <div className="mb-10" data-testid="topics-strip">
              <span className="ash-eyebrow">Browse by Topic</span>
              <div className="mt-4 flex flex-wrap gap-2">
                {topics.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/topics/${t.slug}`}
                    data-testid={`topic-pill-${t.slug}`}
                    className="text-xs uppercase tracking-[0.18em] font-semibold px-3 py-2 border border-[#3A7A8C]/30 text-[#3A7A8C] hover:bg-[#3A7A8C]/5 transition-colors"
                  >
                    {t.category} <span className="text-[#5A5F6A] font-normal">({t.count})</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="blog-grid">
            {visiblePosts.map((p, i) => (
              <Link
                to={`/blog/${p.slug}`}
                key={p.slug}
                data-testid={`blog-card-${i}`}
                className="group bg-white border border-[#e5e7eb] flex flex-col transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#F5F5F5]">
                  <img
                    src={p.featured_image_url}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8 flex flex-col gap-4">
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
                    {p.category}
                  </div>
                  <h3 className="text-2xl font-bold text-[#080808] leading-tight">
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <p className="text-[#3C4048] leading-relaxed line-clamp-4">
                      {p.excerpt}
                    </p>
                  )}
                  <div className="mt-auto flex items-center justify-between pt-4">
                    <span className="text-xs uppercase tracking-[0.18em] text-[#6B7280] font-semibold">
                      {formatDate(p.published_at)}
                    </span>
                    <span className="ash-link-underline">
                      Read <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
